import {createStyles, makeStyles, Theme} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import SystemUpdateAltIcon from "@material-ui/icons/SystemUpdateAlt";
import {BOX_SHADOWS, FONTS, SORT_TYPES} from "../../constants";
import {formatNumber} from "../../helpers/FormatNumber";
import {downloadCSV} from "../../helpers/processBatchData";
import {stateType, stateTypeF} from "../../state";
import {BoxHeader} from "../Common/BoxHeader";
import {FilterButton} from "../Common/FilterButton";
import {calcColumnWidths} from "../DataCleaning/RowSelectTable";
import styles from "../DataCleaning/scroll.module.css";

const CELL_HEIGHT = "30px";
const ROWS_PER_PAGE = 50;

const useStyles = makeStyles((theme: Theme) => {
	return createStyles({
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
			height: "500px",
			transition: "0.5s",
			overflow: "hidden",
		},
		scrollContainer: {
			display: "flex",
			flexDirection: "column",

			backgroundColor: "white",
			height: "470px",
			transition: "0.5s",
			overflow: "auto",
		},
		line: {
			width: "100%",
			height: "1px",
			backgroundColor: "rgba(0,0,0,0.2)",
			position: "relative",
			marginTop: "-10px",
		},
		row: {
			display: "flex",
			flexDirection: "row",
			margin: "10px",
			marginLeft: "0px",
			marginTop: "0px",
		},
		rowWithLine: {
			display: "flex",
			flexDirection: "column",
			"&:hover": {backgroundColor: "rgba(0,0,0,0.05)"},
		},
		header: {
			display: "flex",
			flexDirection: "row",
			margin: "10px",
			marginLeft: "0px",
			paddingLeft: "0px",
			cursor: "pointer",
		},
		cell: {
			fontFamily: FONTS,
			fontSize: "14px",
			lineHeight: "30px",
		},
		headerCell: {
			fontFamily: FONTS,
			fontSize: "14px",
			"&:hover": {color: "rgba(0,0,0,.6)"},
		},
		sortArrow: {
			height: "17px",
			width: "17px",
			marginTop: "2px",
			transition: "0.3s",
		},
		footerContainer: {
			display: "flex",
			flexDirection: "row",
			width: "100%",
			justifyContent: "right",
			marginTop: "10px",
			marginBottom: "10px",
		},
		pageArrowLeft: {
			width: "17px",
			height: "17px",
			marginTop: "2px",
			transform: "rotate(180deg)",
			"&:hover": {transform: "scale(1.2) rotate(180deg)"},
		},
		pageArrowRight: {
			width: "17px",
			height: "17px",
			marginTop: "2px",

			"&:hover": {transform: "scale(1.2)"},
		},
		pageArrowContainer: {
			width: "30px",
			height: "30px",
			cursor: "pointer",
		},
		headerContainer: {display: "flex"},
		filterButtonContainer: {
			marginTop: "25px",
			marginRight: "40px",
			marginLeft: "auto",
			cursor: "pointer",
		},
		filterButton: {"&:hover": {transform: "scale(1.05)"}},
		dropDown: {
			width: "140px",
			height: "30px",
			border: "solid",
			marginLeft: "20px",
			borderRadius: "5px",
			borderColor: "rgba(0,0,0,0.2)",
			borderWidth: "2px",
			marginTop: "-5px",
			display: "flex",
			flexDirection: "row",
			transition: "0.3s",
			cursor: "pointer",
			backgroundColor: "white",
			"&:hover": {backgroundColor: "rgba(0,0,0,0.1)"},
		},
	});
});

type customTableType = {state: stateTypeF; data: any[][]; header: string[]};

export function Line() {
	return (
		<div
			style={{
				width: "100%",
				height: "1px",
				backgroundColor: "rgba(0,0,0,0.2)",
				position: "relative",
				marginTop: "-10px",
				overflow: "hidden",
			}}
		></div>
	);
}

function TableHeader({state, headers, colWidths}) {
	const classes = useStyles();
	return (
		<div
			className={classes.rowWithLine}
			style={{backgroundColor: "rgba(0,0,0,0.05)"}}
		>
			<div className={classes.header}>
				{headers.map((colName, i) => (
					<TableHeadCell
						colName={colName}
						width={colWidths[i]}
						height={CELL_HEIGHT}
						index={i}
						state={state}
					></TableHeadCell>
				))}
			</div>
			<Line></Line>
		</div>
	);
}

type tableRowType = {row: any[]; colWidths: string[]};
export function TableRow({row, colWidths}: tableRowType) {
	const classes = useStyles();
	return (
		<div className={classes.rowWithLine}>
			<div className={classes.row}>
				{row.map((val, i) => (
					<TableCell
						value={val}
						width={colWidths[i]}
						height={CELL_HEIGHT}
						index={i}
					></TableCell>
				))}
			</div>
			<Line></Line>
		</div>
	);
}

type tableCellType = {
	value: string | number;
	width: string;
	height: string;
	index: number;
	state?: stateTypeF;
};

type tableHeadCellType = {
	colName: string;
	width: string;
	height: string;
	index: number;
	state: stateTypeF;
};

function TableCell({value, width, height, index}: tableCellType) {
	const classes = useStyles();
	const isFirst = index === 0;
	return (
		<div
			className={classes.cell}
			style={{
				width: width,
				height: height,
				marginLeft: index === 0 ? "0px" : "20px",
			}}
		>
			<div
				style={{
					textAlign: isFirst ? "left" : "right",
					marginLeft: isFirst ? "10px" : "0px",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{value}
			</div>
		</div>
	);
}

function TableHeadCell({
	colName,
	width,
	height,
	index,
	state,
}: tableHeadCellType) {
	const classes = useStyles();
	const isFirst = index === 0;
	const isSelected = state.batchTableColSort[0] === colName;

	function handleClick(colName: string) {
		function sortFunctionASC(a, b) {
			if (a[index] === b[index]) {
				return 0;
			} else {
				return a[index] > b[index] ? -1 : 1;
			}
		}
		function sortFunctionDESC(a, b) {
			if (a[index] === b[index]) {
				return 0;
			} else {
				return a[index] < b[index] ? -1 : 1;
			}
		}

		let isSameCol = state.batchTableColSort[0] === colName;
		let sortDir = SORT_TYPES.asc;
		if (isSameCol) {
			if (state.batchTableColSort[1] === SORT_TYPES.asc) {
				sortDir = SORT_TYPES.desc;
			}
		}

		const sortFunction =
			sortDir === SORT_TYPES.asc ? sortFunctionASC : sortFunctionDESC;
		let data = state.batchFilteredData;
		data.sort(sortFunction);
		state.setBatchFilteredData(data);

		state.setBatchTableColSort([colName, sortDir]);
	}

	return (
		<div
			className={classes.headerCell}
			style={{
				height: height,
				width: width,
				marginLeft: index === 0 ? "0px" : "20px",
			}}
			onClick={() => handleClick(colName)}
		>
			<div
				style={{
					justifyContent: isFirst ? "left" : "right",
					display: "flex",
				}}
			>
				{isFirst ? (
					<div></div>
				) : (
					<div
						style={{
							height: "0px",
						}}
					>
						{isSelected ? (
							<div
								style={{
									width: "0px",
									marginRight: "10px",
									marginLeft: "-10px",
								}}
							>
								<ArrowDownwardIcon
									className={classes.sortArrow}
									style={{
										transform:
											state.batchTableColSort[1] ===
												SORT_TYPES.asc
												? "rotate(-180deg)"
												: "rotate(0deg)",
									}}
								></ArrowDownwardIcon>
							</div>
						) : (
							<div></div>
						)}
					</div>
				)}
				<div
					style={{
						fontWeight: "bold",
						marginLeft: isFirst ? "10px" : "10px",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{colName}
				</div>
				{!isFirst ? (
					<div></div>
				) : (
					<div style={{height: "0px"}}>
						{isSelected ? (
							<div
								style={{
									width: "0px",
									marginLeft: "10px",
									marginRight: "-10px",
								}}
							>
								<ArrowDownwardIcon
									className={classes.sortArrow}
									style={{
										transform:
											state.batchTableColSort[1] ===
												SORT_TYPES.asc
												? "rotate(-180deg)"
												: "rotate(0deg)",
									}}
								></ArrowDownwardIcon>
							</div>
						) : (
							<div></div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function ScrollTable({state}: stateType) {
	const classes = useStyles();

	if (
		state.saveState.batchPrediction.batchData === undefined ||
		state.batchFilteredData === undefined
	) {
		return <div></div>;
	}
	if (
		state.batchFilteredData.length !== 0 &&
		state.batchFilteredData[0].length !==
		state.saveState.batchPrediction.batchData.data[0].length
	) {
		return <div></div>;
	}
	let headers = state.saveState.batchPrediction.batchData.headers;
	let subData = state.batchFilteredData;

	const colWidths: string[] = [];
	for (let i = 0; i < headers.length; i++) {
		let row = subData.map((row) => row[i]);
		colWidths.push(calcColumnWidths(headers[i], row));
	}

	let page = state.batchTablePage;

	let pageData = subData.slice(
		page * ROWS_PER_PAGE,
		(page + 1) * ROWS_PER_PAGE
	);

	return (
		<div className={styles.scrollContainer}>
			<div style={{width: "fit-content", minWidth: "100%"}}>
				<TableHeader
					state={state}
					headers={headers}
					colWidths={colWidths}
				/>
			</div>
			<div
				style={{width: "fit-content", minWidth: "100%"}}
				className={styles.scrollContainer}
			>
				{pageData.map((row) => (
					<TableRow row={row} colWidths={colWidths}></TableRow>
				))}
			</div>
		</div>
	);
}

function Footer({state}: stateType) {
	const classes = useStyles();
	let page = state.batchTablePage;
	let nRows = formatNumber(state.batchFilteredData.length);
	let start = "0";
	let end = "0";
	if (nRows !== "0") {
		start = formatNumber(page * ROWS_PER_PAGE + 1);
		end = formatNumber(
			Math.min(
				(page + 1) * ROWS_PER_PAGE,
				Number.parseFloat(nRows.replace(",", ""))
			)
		);
		let numPages = Math.ceil(
			Number.parseFloat(nRows.replace(",", "")) / ROWS_PER_PAGE
		);
		if (page > numPages - 1) {
			state.setBatchTablePage(numPages - 1);
		}
	}

	function rightArrowClick() {
		let nPages = Math.ceil(
			Number.parseFloat(nRows.replace(",", "")) / ROWS_PER_PAGE
		);
		if (page < nPages - 1) {
			state.setBatchTablePage(page + 1);
		}
	}

	function leftArrowClick() {
		if (page > 0) {
			state.setBatchTablePage(page - 1);
		}
	}

	return (
		<div className={classes.footerContainer}>
			<Download state={state}></Download>
			<div style={{marginRight: "20px", fontFamily: FONTS}}>
				{" "}
				{start}-{end} of {nRows}
			</div>
			<div
				className={classes.pageArrowContainer}
				onClick={leftArrowClick}
			>
				<ArrowForwardIosIcon className={classes.pageArrowLeft} />
			</div>
			<div
				className={classes.pageArrowContainer}
				style={{marginRight: "20px"}}
				onClick={rightArrowClick}
			>
				<ArrowForwardIosIcon className={classes.pageArrowRight} />
			</div>
		</div>
	);
}

export function Download({state}: stateType) {
	return (
		<div
			style={{
				marginTop: "0px",
				marginLeft: "20px",
				marginRight: "auto",
				display: "flex",
				flexDirection: "row",
				cursor: "pointer",
			}}
			onClick={() =>
				downloadCSV(state.saveState.batchPrediction.batchData)
			}
		>
			<SystemUpdateAltIcon></SystemUpdateAltIcon>
			<div
				style={{
					cursor: "pointer",
					marginLeft: "10px",
					marginTop: "1px",
					fontSize: "13px",
					lineHeight: "23px",
				}}
			>
				Download as CSV
			</div>
		</div>
	);
}

export function PredictedTable({state}: stateType) {
	const classes = useStyles();

	if (
		state.saveState.batchPrediction.batchData === undefined ||
		state.batchFilteredData === undefined
	) {
		return <div></div>;
	}

	return (
		<div className={classes.container}>
			<div className={classes.headerContainer}>
				<BoxHeader
					title={"ML Batch Predictons"}
					toolTip={
						"Your dropped dataset appended with the predicted values from your models. Click 'Download as CSV' to get the file."
					}
					line={false}
				></BoxHeader>
				<FilterButton state={state}></FilterButton>
			</div>
			<ScrollTable state={state}></ScrollTable>
			<Footer state={state}></Footer>
		</div>
	);
}
